<template>
  <transition appear name="fade">
    <div
      v-if="badge"
      class="badge"
      :class="`badge--${backgroundColor} badge--${size}`"
      v-data-test-id="'badge'"
    >
      <component
        :is="badge.icon.component"
        v-if="badge.icon"
        class="badge__icon"
        :class="`badge__icon--${badge.icon.class}`"
      />
      <div class="badge__copy" v-data-test-id="'badge-copy'">
        {{ $t(`${badge.translationKey}`, { var: badge.translationVar }) }}
      </div>
    </div>
  </transition>
</template>

<script>
import { BADGES, BADGE_BACKGROUND_COLORS, BADGE_SIZES } from '@/config/constants'

const PRECEDENCE_ORDERED_BADGES = [
  BADGES.discount50,
  BADGES.discount40,
  BADGES.discount30,
  BADGES.discount20,
  BADGES.soldOut,
  BADGES.exclusive,
  BADGES.limited,
  BADGES.new,
  BADGES.newGeneration,
  BADGES.newColor,
  BADGES.newColors,
  BADGES.sellingFast,
  BADGES.lastChance,
  BADGES.waterproof,
]

export default {
  name: 'Badge',

  props: {
    badgeTypes: {
      type: Array,
      validator(badgeTypes) {
        return badgeTypes.every(type =>
          Object.keys(BADGES).some(badgeKey => BADGES[badgeKey].type === type)
        )
      },
    },

    backgroundColor: {
      type: String,
      validator(value) {
        return Object.values(BADGE_BACKGROUND_COLORS).includes(value)
      },
      default: BADGE_BACKGROUND_COLORS.white,
    },

    size: {
      type: String,
      validator(size) {
        return Object.values(BADGE_SIZES).includes(size)
      },
      default: BADGE_SIZES.normal,
    },
  },

  computed: {
    badge() {
      return PRECEDENCE_ORDERED_BADGES.find(badge => this.badgeTypes.includes(badge.type))
    },
  },
}
</script>

<style lang="scss" scoped>
.badge {
  position: absolute;
  display: flex;
  z-index: 1;
  top: 26px;
  left: 28px;
  padding-left: 2px;
  font-size: $f-size-xxxsmall;
  font-weight: bold;
  letter-spacing: 0.35em;
  text-transform: uppercase;

  &--small {
    font-size: 7px;
    padding: 0;
    letter-spacing: 0.25em;

    @media (min-width: 350px) {
      font-size: $f-size-tiny;
    }

    .badge__copy {
      padding: 6px 8px;
    }

    .badge__icon--new {
      width: 15px;
      height: 15px;
    }
  }

  &--white {
    background-color: transparentize($color-white, 0.15);

    .badge__copy {
      color: $color-text;
    }

    .badge__icon {
      fill: $color-text;
    }
  }

  &--grey {
    background-color: rgba(107, 107, 107, 0.85);
  }

  &--lightgrey {
    background-color: $color-grey-light-4;
  }

  &--blue {
    background-color: rgba(143, 155, 163, 0.85);
  }

  &--grey,
  &--blue {
    .badge__copy {
      color: $color-white;
    }

    .badge__icon {
      fill: $color-white;
    }
  }

  &__copy {
    padding: 9px;
    display: inline-block;
  }

  &__icon {
    display: none;
    position: relative;
    align-self: center;

    @media ($mq-small) {
      display: block;
    }

    &--bolt {
      width: 17px;
      height: 17px;
      margin: 0 -4px 0 6px;
    }

    &--dot {
      height: 6px;
      width: 6px;
      margin: 0 0 0 9px;
    }
  }
}

.fade-enter-active {
  transition: opacity $transition-time-slow;
}

.fade-leave-active {
  transition: opacity $transition-time;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>

<i18n src="@/locales/label.json"></i18n>
